import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTutorialMatchStore } from '../../state/tutorialMatchStore';
import { GameCanvas } from '../components/GameCanvas';
import { HandSelector } from '../components/HandSelector';
import { CompactRunPanel } from '../components/match/CompactRunPanel';
import { TutorialCoach } from '../components/TutorialCoach';
import { ComboReference } from '../components/ComboReference';
import { useGameEffectsCleanup } from '../../hooks/useGameEffectsCleanup';
import { soundManager } from '../../utils/sounds';
import { TUTORIAL_PLAYER_WALLET } from '../../utils/adaptPracticeState';
import { useTutorialEngine } from '../../hooks/useTutorialEngine';

// ── Turn Announcement ─────────────────────────────────────────────────────────

const TurnAnnouncement: React.FC<{
  type: 'your_turn' | 'ai_turn' | 'shop_phase';
  show: boolean;
  onComplete: () => void;
}> = ({ type, show, onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (show) {
      if (type === 'ai_turn') return;
      const duration = type === 'your_turn' ? 500 : 1500;
      const timer = setTimeout(() => onCompleteRef.current(), duration);
      return () => clearTimeout(timer);
    }
  }, [show, type]);

  if (!show) return null;

  const config = {
    your_turn: {
      text: 'YOUR TURN',
      color: 'text-arcade-cyan',
      glow: 'drop-shadow-[0_0_20px_rgba(0,255,255,0.8)]',
    },
    ai_turn: {
      text: 'AI IS THINKING...',
      color: 'text-amber-400',
      glow: 'drop-shadow-[0_0_15px_rgba(255,180,0,0.6)]',
    },
    shop_phase: {
      text: 'SHOP IS OPEN',
      color: 'text-emerald-400',
      glow: 'drop-shadow-[0_0_15px_rgba(0,255,150,0.6)]',
    },
  }[type];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 2.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
    >
      <motion.div
        initial={{ y: -30 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        className={`font-display text-2xl font-bold sm:text-4xl md:text-5xl ${config.color} ${config.glow}`}
      >
        {config.text}
      </motion.div>
    </motion.div>
  );
};

// ── Mechanics learned checklist ───────────────────────────────────────────────

const MECHANIC_CHECKS = [
  { id: 'cards', label: 'Card combos & scoring' },
  { id: 'turns', label: 'Turn-by-turn play' },
  { id: 'shop', label: 'Shop & jokers' },
  { id: 'hearts', label: 'Hearts system' },
];

// ── Match Result Modal ────────────────────────────────────────────────────────

const TutorialResultModal: React.FC<{
  winner: string;
  playerScore: number;
  aiScore: number;
  mechanicsEncountered: string[];
  onPlayAgain: () => void;
  onRealMatch: () => void;
  onReviewGuide: () => void;
}> = ({
  winner,
  playerScore,
  aiScore,
  mechanicsEncountered,
  onPlayAgain,
  onRealMatch,
  onReviewGuide,
}) => {
  const playerWon = winner === 'player';

  useEffect(() => {
    soundManager.play(playerWon ? 'win' : 'lose');
  }, [playerWon]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="arcade-panel p-4 text-center space-y-4 max-w-md w-full sm:p-8 sm:space-y-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="space-y-2">
          <div
            className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center text-4xl ${
              playerWon
                ? 'bg-green-500/20 border-2 border-green-500/50'
                : 'bg-red-500/20 border-2 border-red-500/50'
            }`}
          >
            {playerWon ? '\uD83C\uDFC6' : '\uD83D\uDC94'}
          </div>
          <h2
            className={`font-display text-3xl font-bold ${
              playerWon ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {playerWon ? 'VICTORY!' : 'DEFEAT'}
          </h2>
          <p className="text-sm text-white/60">Target score reached</p>
        </div>

        <div className="flex items-center justify-center gap-4 sm:gap-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">YOU</div>
            <div className="font-display text-3xl font-bold text-arcade-cyan sm:text-4xl">
              {playerScore}
            </div>
          </div>
          <div className="font-display text-xl font-bold text-white/30 sm:text-2xl">VS</div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">AI</div>
            <div className="font-display text-3xl font-bold text-red-400 sm:text-4xl">
              {aiScore}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-left">
          <h3 className="text-xs uppercase tracking-widest text-white/40 mb-2">
            Mechanics Encountered
          </h3>
          <div className="space-y-1">
            {MECHANIC_CHECKS.map((m) => {
              const encountered = mechanicsEncountered.includes(m.id);
              return (
                <div key={m.id} className="flex items-center gap-2 text-sm">
                  <span className={encountered ? 'text-green-400' : 'text-white/20'}>
                    {encountered ? '\u2713' : '\u2013'}
                  </span>
                  <span className={encountered ? 'text-white/80' : 'text-white/30'}>{m.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <button
            type="button"
            onClick={onPlayAgain}
            className="flex-1 arcade-btn bg-gradient-to-r from-purple-600 to-pink-600"
          >
            Play Another Tutorial
          </button>
          <button
            type="button"
            onClick={onRealMatch}
            className="flex-1 arcade-btn bg-arcade-cyan/20 text-arcade-cyan hover:bg-arcade-cyan/30"
          >
            Try Real Match
          </button>
          <button
            type="button"
            onClick={onReviewGuide}
            className="flex-1 arcade-btn bg-white/10 hover:bg-white/20"
          >
            Review Guide
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export const TutorialMatchPage: React.FC = () => {
  const navigate = useNavigate();
  const engine = useTutorialEngine();
  const { comboDrawerOpen, toggleComboDrawer, mechanicsLearned } = useTutorialMatchStore();

  useGameEffectsCleanup();

  const {
    state: engineState,
    currentPhase,
    coachConfig,
    showCoach,
    coachActionCompleted,
    clientMatchState,
    isMyTurn,
    isShopPhase,
    selectedCards,
    handleCardClick,
    handlePlayHand,
    handleDismiss,
    handleSkipAll,
    handlePurchase,
    handleShopReady,
    handlePlayAgain: enginePlayAgain,
    handleSelectionChange,
  } = engine;

  // ── Turn announcements ──────────────────────────────────────────────────────
  const [showTurnAnnouncement, setShowTurnAnnouncement] = useState(false);
  const [turnAnnouncementType, setTurnAnnouncementType] = useState<
    'your_turn' | 'ai_turn' | 'shop_phase'
  >('your_turn');
  const prevPhaseRef = useRef<string>('');

  useEffect(() => {
    const phase = currentPhase;
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;

    if (prev === phase) return;

    if (phase === 'guide_select' && prev !== 'await_play') {
      setTurnAnnouncementType('your_turn');
      setShowTurnAnnouncement(true);
    } else if (phase === 'ai_thinking') {
      setTurnAnnouncementType('ai_turn');
      setShowTurnAnnouncement(true);
    } else if (phase === 'shop_intro' || phase === 'shop_active') {
      if (prev !== 'shop_intro' && prev !== 'explain_joker') {
        setTurnAnnouncementType('shop_phase');
        setShowTurnAnnouncement(true);
      }
    } else {
      setShowTurnAnnouncement(false);
    }
  }, [currentPhase]);

  // ── Derived state from engine ───────────────────────────────────────────────
  const playerScore = engineState.playerScore;
  const aiScore = engineState.aiScore;
  const targetScore = 500;
  const timeline = engineState.timeline;

  const myRunState = clientMatchState.playerRunStates?.[TUTORIAL_PLAYER_WALLET];
  const myShopState = clientMatchState.playerShopStates?.[TUTORIAL_PLAYER_WALLET];

  // ── Navigation handlers ─────────────────────────────────────────────────────
  const handlePlayAgain = () => {
    enginePlayAgain();
  };

  const handleRealMatch = () => {
    navigate('/matchmaking');
  };

  const handleReviewGuide = () => {
    navigate('/tutorial');
  };

  // ── Main game layout ──────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col overflow-hidden pt-14">
      {/* Tutorial banner */}
      <div className="shrink-0 bg-arcade-cyan/10 border-b border-arcade-cyan/20 px-4 py-1.5 text-center text-xs text-arcade-cyan font-semibold">
        TUTORIAL MODE — 500 pts to win (simulated)
      </div>

      {/* Match Result Modal */}
      {engineState.isCompleted && engineState.winner && (
        <TutorialResultModal
          winner={engineState.winner}
          playerScore={playerScore}
          aiScore={aiScore}
          mechanicsEncountered={mechanicsLearned}
          onPlayAgain={handlePlayAgain}
          onRealMatch={handleRealMatch}
          onReviewGuide={handleReviewGuide}
        />
      )}

      {/* Turn Announcements */}
      <AnimatePresence>
        {showTurnAnnouncement && (
          <TurnAnnouncement
            type={turnAnnouncementType}
            show={showTurnAnnouncement}
            onComplete={() => setShowTurnAnnouncement(false)}
          />
        )}
      </AnimatePresence>

      {/* Coach tip overlay */}
      {showCoach && coachConfig && (
        <TutorialCoach
          tipId={currentPhase}
          title={coachConfig.title}
          body={coachConfig.body}
          category={coachConfig.category}
          highlightSelector={coachConfig.highlightSelector}
          requiredAction={coachConfig.requiredAction}
          actionCompleted={coachConfig.requiredAction ? coachActionCompleted : undefined}
          dismissLabel={coachConfig.dismissLabel}
          show={true}
          onDismiss={handleDismiss}
          onSkipAll={handleSkipAll}
        />
      )}

      {/* Combo reference drawer */}
      <ComboReference isOpen={comboDrawerOpen} onToggle={toggleComboDrawer} />

      {/* Main content */}
      <div className="flex-1 flex flex-col gap-2 p-2 overflow-hidden">
        {/* Score bar */}
        <div className="shrink-0 flex items-center justify-between rounded-lg border border-white/10 bg-black/40 px-3 py-1.5 backdrop-blur">
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">YOU</span>
            <span className="text-base font-bold text-neon-400">{playerScore}</span>
            <div className="hidden sm:block h-1 w-16 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full bg-neon-400 transition-all"
                style={{ width: `${Math.min(100, (playerScore / targetScore) * 100)}%` }}
              />
            </div>
          </div>

          <div className="text-center">
            <div className="text-[10px] text-white/40">
              {isMyTurn ? (
                <span className="text-arcade-cyan">Your Turn</span>
              ) : isShopPhase ? (
                <span className="text-emerald-400">Shop</span>
              ) : (
                <span className="text-amber-400">AI Turn</span>
              )}
            </div>
            <div className="text-[10px] text-white/30">T{timeline.length + 1}</div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:block h-1 w-16 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full bg-red-400 transition-all"
                style={{ width: `${Math.min(100, (aiScore / targetScore) * 100)}%` }}
              />
            </div>
            <span className="text-base font-bold text-red-400">{aiScore}</span>
            <span className="text-xs text-white/50">AI</span>
          </div>
        </div>

        {/* Main Game Grid */}
        <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[1fr,minmax(220px,280px)] gap-2 min-h-0 overflow-hidden">
          {/* Left column: Canvas + Hand */}
          <div className="flex-1 flex flex-col gap-2 min-h-0">
            {/* Game Canvas */}
            <div data-tutorial="game-canvas" className="flex-1 min-h-0">
              <GameCanvas
                state={clientMatchState}
                selectedCardIds={selectedCards}
                jokers={myRunState?.jokers ?? []}
                isInteractive={isMyTurn && !isShopPhase}
                onCardClick={handleCardClick}
              />
            </div>

            {/* Hand Selector */}
            <div data-tutorial="hand-selector" className="shrink-0 h-[44px] lg:h-[56px]">
              <HandSelector
                state={clientMatchState}
                isMyTurn={isMyTurn}
                selectedCardIds={selectedCards}
                onSelectionChange={handleSelectionChange}
                onPlay={handlePlayHand}
                walletOverride={TUTORIAL_PLAYER_WALLET}
              />
            </div>
          </div>

          {/* Right sidebar: Run panel (desktop) */}
          <div
            data-tutorial="shop-panel"
            className="hidden lg:flex flex-col gap-2 min-h-0 overflow-y-auto"
          >
            {/* Opponent info */}
            <div className="rounded-lg border border-white/10 bg-black/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-white/50 uppercase tracking-wide">AI (Rookie)</span>
                <span className="text-xs text-red-400 font-bold">{aiScore} pts</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-white/50">
                <span>{'\u2764\uFE0F'} 3</span>
                <span>0 chips</span>
                <span>0 jokers</span>
              </div>
            </div>

            {/* Player's run panel + shop */}
            <div className="flex-1 min-h-0 overflow-auto">
              <CompactRunPanel
                runState={myRunState}
                shopState={myShopState}
                phase={clientMatchState.phase}
                onBuy={handlePurchase}
                onReady={handleShopReady}
              />
            </div>

            {/* Back to tutorial */}
            <button
              type="button"
              onClick={() => navigate('/tutorial')}
              className="w-full py-1.5 text-xs text-white/40 hover:text-white/60 border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
            >
              Back to Tutorial
            </button>
          </div>
        </div>

        {/* Mobile bottom bar */}
        <div className="lg:hidden shrink-0 flex items-center justify-between rounded-lg border border-white/10 bg-black/40 px-3 py-1.5 backdrop-blur">
          <div className="flex items-center gap-3 text-xs">
            <span className="text-red-400">
              {'\u2764\uFE0F'} {engineState.playerHearts}
            </span>
            <span className="text-yellow-400">{engineState.playerChips} chips</span>
            <span className="text-purple-400">{engineState.playerJokers.length} jokers</span>
          </div>
          <div className="flex items-center gap-2">
            {isShopPhase && (
              <button
                type="button"
                onClick={handleShopReady}
                className="text-xs px-3 py-1 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
              >
                Ready
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/tutorial')}
              className="text-xs text-white/40 hover:text-white/60"
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
